import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, Gift } from "lucide-react";

export const ChristmasURAGenerator = () => {
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["natal"]);
  const [recessDates, setRecessDates] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [generatedScript, setGeneratedScript] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleGenerate = async () => {
    if (!companyName.trim() || !companyType.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha o nome e tipo da empresa.",
        variant: "destructive",
      });
      return;
    }

    if (selectedTypes.length === 0) {
      toast({
        title: "Selecione pelo menos um tipo",
        description: "Escolha pelo menos um tipo de mensagem para gerar o script.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratedScript("");

    try {
      const { data, error } = await supabase.functions.invoke('generate-ura-script', {
        body: {
          companyName: companyName.trim(),
          companyType: companyType.trim(),
          messageTypes: selectedTypes,
          recessDates: recessDates.trim(),
          website: website.trim(),
          email: email.trim(),
          isChristmas: true,
        },
      });

      if (error) throw error;

      if (data?.script) {
        setGeneratedScript(data.script);
        toast({
          title: "Script gerado com sucesso!",
          description: "Seu script de URA temático foi criado pela IA.",
        });
      }
    } catch (error) {
      console.error('Erro ao gerar script:', error);
      toast({
        title: "Erro ao gerar script",
        description: "Não foi possível gerar o script. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section id="christmas-ura-generator" className="py-20 px-4 bg-gradient-to-br from-red-900/10 via-green-900/10 to-background relative overflow-hidden">
      {/* Christmas decorations */}
      <div className="absolute top-10 left-10 text-6xl opacity-10 animate-float" aria-hidden="true">🎄</div>
      <div className="absolute top-20 right-20 text-5xl opacity-10 animate-float" style={{ animationDelay: '1s' }} aria-hidden="true">🎅</div>
      <div className="absolute bottom-20 left-1/4 text-4xl opacity-10 animate-float" style={{ animationDelay: '2s' }} aria-hidden="true">🎁</div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <Gift className="w-6 h-6 text-red-500 animate-pulse" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              🎄 Gerador de Texto para URA de Boas Festas
            </h2>
            <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
          </div>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto drop-shadow-md">
            Nossa inteligência artificial cria textos profissionais e humanizados para suas URAs de fim de ano, com mensagens de Natal, Boas Festas e Feliz 2026! 🎊
          </p>
        </div>

        <div className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-sm rounded-lg shadow-2xl p-6 lg:p-8 space-y-6 border-2 border-red-200/50">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="companyName">Nome da Empresa *</Label>
              <Input
                id="companyName"
                placeholder="Ex: Clínica Saúde Total"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={isGenerating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyType">Tipo de Empresa *</Label>
              <Input
                id="companyType"
                placeholder="Ex: Clínica Médica, Loja Virtual, etc."
                value={companyType}
                onChange={(e) => setCompanyType(e.target.value)}
                disabled={isGenerating}
              />
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="website">Site (opcional)</Label>
              <Input
                id="website"
                placeholder="Ex: www.suaempresa.com.br"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={isGenerating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mail (opcional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="Ex: contato@suaempresa.com.br"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isGenerating}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Tipos de Mensagem (selecione um ou mais)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: "natal", label: "🎄 Natal", icon: "🎄" },
                { value: "boas-festas", label: "🎊 Boas Festas", icon: "🎊" },
                { value: "recesso", label: "⏰ Recesso de Final de Ano", icon: "⏰" },
                { value: "ano-novo", label: "🎆 Feliz 2026", icon: "🎆" },
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeToggle(type.value)}
                  disabled={isGenerating}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    selectedTypes.includes(type.value)
                      ? "border-red-500 bg-red-50 shadow-md"
                      : "border-gray-200 bg-white hover:border-red-300"
                  } ${isGenerating ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors
                    ${selectedTypes.includes(type.value) ? 'border-yellow-400 bg-yellow-400' : 'border-gray-300'}">
                    {selectedTypes.includes(type.value) && (
                      <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              Selecione os temas que deseja incluir na sua mensagem de fim de ano
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recessDates">Datas de Recesso (opcional)</Label>
            <Input
              id="recessDates"
              placeholder="Ex: 23/12/2025 a 02/01/2026"
              value={recessDates}
              onChange={(e) => setRecessDates(e.target.value)}
              disabled={isGenerating}
            />
            <p className="text-sm text-muted-foreground">
              Informe o período de recesso se aplicável
            </p>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white border-2 border-yellow-400/30"
            size="lg"
            aria-label={isGenerating ? "Gerando script de URA temático" : "Gerar script de URA de Boas Festas"}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                Gerando seu script temático...
              </>
            ) : (
              <>
                <Gift className="mr-2 h-5 w-5" aria-hidden="true" />
                🎄 Gere seu Texto de URA de Boas Festas
              </>
            )}
          </Button>

          {generatedScript && (
            <div className="space-y-3 animate-fade-in">
              <Label>Seu Script de URA de Boas Festas Personalizado 🎅</Label>
              <Textarea
                value={generatedScript}
                readOnly
                className="min-h-[300px] font-mono text-sm bg-green-50/50 border-green-200"
              />
              <p className="text-sm text-muted-foreground bg-yellow-50/50 p-3 rounded-md border border-yellow-200">
                ✨ Copie o script acima e use como base para sua URA de fim de ano. Você pode editá-lo conforme necessário para adicionar informações específicas da sua empresa.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
